import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import ListingCard from "@/components/listing/ListingCard";
import SEO from "@/components/SEO";
import { useUserLocation, listingDistanceFrom, formatDistance } from "@/hooks/useUserLocation";
import { Navigation, MapPin, Loader2, ShieldCheck } from "lucide-react";

const QUICK_FILTERS = [
  { label: "Braiders & stylists", key: "braid", type: "service" },
  { label: "Caterers & food", key: "jollof", type: "service" },
  { label: "Restaurants", key: "restaurant", type: "service" },
  { label: "Tailors", key: "tailor", type: "service" },
  { label: "African grocery", key: "grocery", type: "product" },
  { label: "Childcare", key: "child", type: "service" },
  { label: "Photographers", key: "photo", type: "service" },
  { label: "Event planners", key: "event", type: "service" },
];

const NearMe = () => {
  const { coords, status, requestLocation } = useUserLocation();
  const [filter, setFilter] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 120 };
    if (filter?.type) params.type = filter.type;
    if (filter?.key) params.q = filter.key;
    api.get("/listings", { params })
      .then((r) => setListings(r.data || []))
      .finally(() => setLoading(false));
  }, [filter]);

  // Sort by distance once we have coords
  const sorted = useMemo(() => {
    if (!coords) return listings;
    return [...listings]
      .map((l) => ({ ...l, _distance_km: listingDistanceFrom(l, coords) }))
      .filter((l) => l._distance_km !== null)
      .sort((a, b) => a._distance_km - b._distance_km);
  }, [coords, listings]);

  return (
    <div>
      <SEO
        title="African businesses near me — braiders, caterers, restaurants"
        description="Find African food, braiders, caterers, restaurants, tailors and local services near you. AfriMarket Near Me uses your location to surface verified businesses in your area."
        path="/near-me"
        noindex
      />

      <section className="container-narrow py-10">
        <div className="max-w-3xl">
          <div className="label-overline text-[#C05A3C] mb-2 flex items-center gap-2">
            <Navigation className="w-3.5 h-3.5" /> Near you
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">African businesses near me</h1>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            Allow location access to discover verified African food sellers, braiders, caterers, restaurants and services in your neighbourhood — sorted by distance.
          </p>
        </div>

        {/* Location status */}
        {status !== "ok" && (
          <Card className="mt-6 p-6 bg-white border-black/10">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#C05A3C]/10 flex items-center justify-center shrink-0">
                <MapPin className="w-6 h-6 text-[#C05A3C]" />
              </div>
              <div className="flex-1">
                <div className="font-display text-lg">
                  {status === "denied" ? "Location access denied" : status === "unavailable" ? "Location unavailable" : "Share your location"}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {status === "denied"
                    ? "Enable location in your browser settings to use Near Me, or browse by city instead."
                    : "We'll only use your location to sort sellers by distance — never stored or shared."}
                </div>
              </div>
              <Button onClick={requestLocation} data-testid="enable-location-btn" className="bg-[#C05A3C] hover:bg-[#D46B4E]">
                Enable location
              </Button>
            </div>
          </Card>
        )}

        {/* Quick filter chips */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="quick-filters">
          <button
            onClick={() => setFilter(null)}
            className={`text-sm px-4 py-2 rounded-full border transition-colors ${!filter ? "bg-[#1A2024] text-white border-[#1A2024]" : "bg-white border-black/10 hover:border-[#C05A3C]"}`}
            data-testid="filter-all"
          >
            All
          </button>
          {QUICK_FILTERS.map((qf) => (
            <button
              key={qf.key}
              onClick={() => setFilter(qf)}
              className={`text-sm px-4 py-2 rounded-full border transition-colors ${filter?.key === qf.key ? "bg-[#C05A3C] text-white border-[#C05A3C]" : "bg-white border-black/10 hover:border-[#C05A3C]"}`}
              data-testid={`filter-${qf.key}`}
            >
              {qf.label}
            </button>
          ))}
        </div>
      </section>

      {/* Results */}
      <section className="container-narrow pb-16">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-12 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Finding nearby sellers...
          </div>
        ) : sorted.length === 0 ? (
          <Card className="p-10 text-center bg-white border-black/10">
            <MapPin className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              {coords
                ? "No nearby sellers match this filter yet."
                : "Enable location to see distances. Without it, you'll see latest listings instead."}
            </p>
            {!coords && listings.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8 text-left">
                {listings.slice(0, 6).map((l) => <ListingCard key={l.listing_id} listing={l} />)}
              </div>
            )}
          </Card>
        ) : (
          <>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <ShieldCheck className="w-4 h-4 text-[#4A5D23]" />
              {sorted.length} verified sellers sorted by distance
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="near-me-results">
              {sorted.slice(0, 24).map((l) => (
                <div key={l.listing_id} className="relative">
                  <ListingCard listing={l} />
                  {l._distance_km !== null && (
                    <div className="absolute top-3 right-3 bg-white/95 backdrop-blur text-xs font-semibold px-2 py-1 rounded-full border border-black/10" data-testid={`distance-${l.listing_id}`}>
                      {formatDistance(l._distance_km)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <section className="container-narrow pb-16">
        <Card className="bg-[#1A2024] text-white rounded-2xl border-0 p-8 text-center">
          <h2 className="font-display text-2xl">Want to browse by city instead?</h2>
          <p className="mt-2 text-white/80">Explore African businesses by country, city or local area.</p>
          <Link to="/locations" className="inline-block mt-5">
            <Button className="bg-[#C05A3C] hover:bg-[#D46B4E] rounded-md h-12 px-8 font-semibold">
              Browse all locations
            </Button>
          </Link>
        </Card>
      </section>
    </div>
  );
};

export default NearMe;
